

// src/pages/ROICalculator/index.jsx

import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Components
import EnhancedSEO from '../../Components/SEO/EnhancedSEO';
import CalculatorForm from './CalculatorForm';
import ResultsDisplay from './ResultsDisplay';
import DataSourcesModal from './DataSourcesModal';
import AssumptionsModal from './AssumptionsModal';

// Constants
import { DATA_SOURCES, ASSUMPTIONS } from './constants';

const ROICalculatorPage = () => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [showDataSources, setShowDataSources] = useState(false);
  const [showAssumptions, setShowAssumptions] = useState(false);

  const handleCalculate = (calculatedResults) => {
    setResults(calculatedResults);
    setShowResults(true);
    
    // Smooth scroll to results
    setTimeout(() => {
      document.getElementById('results-section')?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }, 100);
  };

  const handleStartFreeTrial = () => {
    navigate("/auth/signin", { state: { name: 'roi-calculator', link: '/roi-calculator' } });
  };

  const handleWatchDemo = () => {
    navigate('/demo');
  };

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How accurate is the Revfer ROI calculator?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our ROI calculator uses data from authoritative sources including Harvard Business School, McKinsey & Company, HubSpot, and the U.S. Bureau of Labor Statistics. All assumptions are conservative, meaning actual results often exceed calculator projections. We provide full transparency on data sources and calculation methodology."
        }
      },
      {
        "@type": "Question",
        "name": "What factors affect my ROI with Revfer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Your ROI depends on: current customer message volume, manual time spent on tasks AI can automate, current rating and review count, monthly revenue, and industry type. Businesses with higher message volumes and more manual processes see larger returns."
        }
      },
      {
        "@type": "Question",
        "name": "How quickly will I see ROI?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most businesses see time savings immediately on day 1. Cost savings are measurable within the first week. Revenue impacts from improved ratings typically manifest over 1-3 months. Average payback period is less than 1 month."
        }
      },
      {
        "@type": "Question",
        "name": "Is the ROI calculator's data scientifically backed?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. All calculations are based on peer-reviewed research and authoritative business sources including Harvard Business School studies, McKinsey research, and government labor statistics. We provide complete source citations for verification."
        }
      }
    ]
  };

  // Calculator Tool Schema
  const calculatorSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Revfer ROI Calculator",
    "description": "Calculate time savings, cost reduction, and revenue impact from implementing AI automation for your small business",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Time savings calculation",
      "Cost reduction analysis",
      "Revenue impact projection",
      "Industry-specific calculations",
      "Data source transparency"
    ]
  };

  return (
    <>
      {/* Enhanced SEO */}
      <EnhancedSEO 
        title="ROI Calculator - Calculate Your Savings with AI | Revfer"
        description="Calculate exactly how much time and money you'll save with Revfer's AI automation. See your ROI in hours saved, cost reduction, and revenue increase. Based on research from Harvard, McKinsey, and HubSpot. Free calculator with transparent data sources."
        keywords="ROI calculator, AI ROI calculator, small business ROI, business automation savings, calculate AI savings, ChatGPT ROI, customer service ROI, AI cost savings calculator, business efficiency calculator, time savings calculator, revenue impact calculator, small business tools"
        type="website"
        image="/images/roi-calculator-og.jpg"
        author="Revfer Analytics Team"
        dateModified={new Date().toISOString()}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'ROI Calculator', url: '/roi-calculator' }
        ]}
      />

      {/* Structured Data */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(calculatorSchema)}
        </script>

        {/* Additional Meta Tags */}
        <meta name="calculator-type" content="Business ROI, AI Savings, Cost Analysis" />
        <meta name="data-sources" content="Harvard Business School, McKinsey, HubSpot, U.S. Bureau of Labor Statistics" />
        <meta name="calculation-methodology" content="Conservative estimates, peer-reviewed research, transparent assumptions" />
        <meta name="target-audience" content="Small Business Owners, Entrepreneurs, Business Managers" />
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e3f2fd 100%)',
          py: { xs: 6, md: 10 },
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 2,
                  lineHeight: 1.2
                }}
              >
                Calculate Your ROI with Revfer
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  color: 'text.secondary',
                  mb: 4,
                  lineHeight: 1.6
                }}
              >
                See exactly how much time and money you'll save with AI automation.
                Based on research from Harvard, McKinsey, and other authoritative sources.
              </Typography>

              {/* Trust Badges */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssessmentIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Research-Backed Data
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Conservative Estimates
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalculateIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    100% Transparent
                  </Typography>
                </Box>
              </Box>

              {/* Data Source Links */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowDataSources(true)}
                  sx={{ textTransform: 'none' }}
                >
                  View Data Sources
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowAssumptions(true)}
                  sx={{ textTransform: 'none' }}
                >
                  View Assumptions
                </Button>
              </Box>
            </Grid>

            {isMobile && <Grid item xs={12} />}

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Paper
                  elevation={6}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'white',
                    maxWidth: 500
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                    Typical Results
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700 }}>
                          7.5
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          hours saved
                          <br />per week
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 700 }}>
                          $8K
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          cost savings
                          <br />per year
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: 'info.main', fontWeight: 700 }}>
                          3,100%
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          average
                          <br />ROI
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Calculator Form Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <CalculatorForm onCalculate={handleCalculate} />
      </Container>

      {/* Results Section */}
      {showResults && results && (
        <Fade in={showResults} timeout={800}>
          <Box id="results-section" sx={{ bgcolor: '#f5f7fa', py: { xs: 6, md: 8 } }}>
            <Container maxWidth="lg">
              <ResultsDisplay 
                results={results}
                onStartTrial={handleStartFreeTrial}
                onWatchDemo={handleWatchDemo}
              />
            </Container>
          </Box>
        </Fade>
      )}

      {/* Methodology Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ mb: 2, fontWeight: 600 }}>
            Our Methodology
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 800, mx: 'auto' }}>
            We believe in complete transparency. Our calculations are based on peer-reviewed
            research and authoritative business sources. All assumptions are conservative,
            meaning your actual results often exceed these projections.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
              <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Research-Backed
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Every data point comes from authoritative sources including Harvard Business
                School, McKinsey & Company, HubSpot, and U.S. government statistics.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
              <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Conservative Estimates
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                When research provides a range, we use the lower or middle value. This means
                actual results typically exceed calculator projections.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
              <CalculateIcon sx={{ fontSize: 40, color: 'info.main', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                100% Transparent
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                View our complete data sources and calculation assumptions. We show exactly
                how every number is derived so you can trust the results.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={() => setShowDataSources(true)}
            sx={{ mr: 2, textTransform: 'none' }}
          >
            View All Data Sources
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShowAssumptions(true)}
            sx={{ textTransform: 'none' }}
          >
            View All Assumptions
          </Button>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'primary.main', py: { xs: 6, md: 8 }, color: 'white' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 600 }}>
              Ready to Start Saving?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Join hundreds of businesses already using Revfer to automate operations
              and grow revenue.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleStartFreeTrial}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'grey.100' },
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem'
                }}
              >
                Start Free Trial →
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleWatchDemo}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': { borderColor: 'grey.100', bgcolor: 'rgba(255,255,255,0.1)' },
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem'
                }}
              >
                Watch Demo
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Modals */}
      <DataSourcesModal
        open={showDataSources}
        onClose={() => setShowDataSources(false)}
        sources={DATA_SOURCES}
      />
      <AssumptionsModal
        open={showAssumptions}
        onClose={() => setShowAssumptions(false)}
        assumptions={ASSUMPTIONS}
      />
    </>
  );
};

export default ROICalculatorPage;